import {
  Box,
  Pagination,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Avatar,
  Stack
} from '@mui/material';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import TableCell from '../../../components/TableCell';
import { getContractsHistory } from '../../../redux/slices/contractsSlice';
import { RootState } from '../../../redux/store';
import { convertSecondsToHHMMSS } from '../../../helpers/time';

interface Props {
  scooterId?: string;
  open: boolean;
}

const HistoryContent = ({ scooterId, open }: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { items: contracts, pages } = useSelector(
    (state: RootState) => state.contracts.history
  );
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (open && scooterId) {
      dispatch(
        getContractsHistory({
          scooterId,
          page,
          limit: 20,
          active: false
        })
      );
    } else {
      setPage(1);
    }
  }, [dispatch, scooterId, open, page]);

  const getAvatarColor = (name: string) => {
    const colors = ['#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800', '#FF5722'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const getUserInitial = (user: any) => {
    if (user.firstName) {
      return user.firstName.charAt(0).toUpperCase();
    }
    return 'K';
  };

  return (
    <Box>
      <TableContainer sx={{ maxHeight: 500 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('scooterModal.colClient')}</TableCell>
              <TableCell>{t('scooterModal.colPhone')}</TableCell>
              <TableCell>{t('scooterModal.colScooterNumber')}</TableCell>
              <TableCell>{t('scooterModal.colTripStart')}</TableCell>
              <TableCell>{t('scooterModal.colTripEnd')}</TableCell>
              <TableCell>{t('scooterModal.colRidingTime')}</TableCell>
              <TableCell>{t('scooterModal.colPauseTime')}</TableCell>
              <TableCell>{t('scooterModal.colBookingTime')}</TableCell>
              <TableCell>{t('scooterModal.colTripDuration')}</TableCell>
              <TableCell>{t('scooterModal.colDistance')}</TableCell>
              <TableCell>{t('scooterModal.colCost')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contracts.map(contract => {
              const userInitial = getUserInitial(contract.user);
              const avatarColor = getAvatarColor(contract.user.firstName || 'K');
              
              return (
                <TableRow key={contract._id}>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: avatarColor,
                          fontSize: '0.875rem',
                          fontWeight: 600
                        }}
                      >
                        {userInitial}
                      </Avatar>
                      <Typography variant="body2">
                        {contract.user.lastName} {contract.user.firstName}{' '}
                        {contract.user.middleName}
                      </Typography>
                    </Stack>
                  </TableCell>
                <TableCell>{contract.user.phone}</TableCell>
                <TableCell>{contract.scooter?.name}</TableCell>
                <TableCell>
                  {moment(contract.createdAt).format('DD MMM YYYY, HH:mm:ss')}
                </TableCell>
                <TableCell>
                  {moment(contract.closedDate).format('DD MMM YYYY, HH:mm:ss')}
                </TableCell>
                <TableCell>
                  {contract?.historyDetails?.normal?.duration
                    ? convertSecondsToHHMMSS(
                        contract?.historyDetails?.normal?.duration
                      )
                    : '-'}
                </TableCell>
                <TableCell>
                  {contract?.historyDetails?.pause?.duration
                    ? convertSecondsToHHMMSS(
                        contract?.historyDetails?.pause?.duration
                      )
                    : '-'}
                </TableCell>
                <TableCell>
                  {(contract?.historyDetails as any)?.book?.duration
                    ? convertSecondsToHHMMSS(
                        (contract?.historyDetails as any)?.book?.duration
                      )
                    : '-'}
                </TableCell>
                  <TableCell>
                    {moment.utc(contract.period * 1000).format('mm:ss')}
                  </TableCell>
                  <TableCell>{contract.mileage || 0} {t('scooterModal.distanceUnit')}</TableCell>
                  <TableCell>{contract.sum || 0} {t('scooterModal.currency')}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      
      {pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {contracts.length === 0 && (
        <Typography textAlign="center" sx={{ py: 4 }}>
          {t('scooterModal.noTripHistory')}
        </Typography>
      )}
    </Box>
  );
};

export default HistoryContent;
